import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  Typography,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import { DragIndicator, Delete, ExpandLess, ExpandMore } from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { useSettings } from '../../context/SettingsContext';
import EnhancedRichTextEditor from '../EnhancedRichTextEditor';

/** Readable names for slide types. */
const SLIDE_TYPE_LABELS = {
  title_slide: 'Title slide',
  content_slide: 'Content slide',
  image_slide: 'Image and caption',
  section_header: 'Section header',
  data_table: 'Data table',
  custom: 'Custom layout',
};

/**
 * Single draggable slide card with rich text editing.
 * @param {object} props - Component props.
 * @param {object} props.slide - Slide model `{ id, type, content }`.
 * @param {number} props.index - Slide index (0-based).
 * @param {function(string, string, *): void} props.updateSlide - Field updater.
 * @param {function(string): void} props.removeSlide - Remove by id.
 * @param {function(number, number): void} props.moveSlide - Reorder slides.
 * @param {boolean} props.collapsed - Collapsed state.
 * @param {function(string): void} props.onToggleCollapsed - Toggle collapse by slide id.
 * @param {boolean} [props.readOnly] - When true, show HTML preview only (preview mode).
 * @returns {React.ReactElement}
 */
function DraggableSlide({
  slide,
  index,
  updateSlide,
  removeSlide,
  moveSlide,
  collapsed,
  onToggleCollapsed,
  readOnly = false,
}) {
  const ref = useRef(null);
  const editorRef = useRef(null);
  const { state: settings } = useSettings();

  const [, drop] = useDrop({
    accept: 'slide',
    hover(item, monitor) {
      if (!ref.current || readOnly) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveSlide(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'slide',
    item: () => ({ id: slide.id, index }),
    canDrag: !readOnly,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  preview(drop(ref));

  const handleContentChange = (html) => {
    updateSlide(slide.id, 'content', html);
  };

  /**
   * Preview HTML for the slide: {{variables}} wrapped in a highlight mark, then sanitized.
   * slide.content is stored template JSON that any account member (or an imported deck via
   * the AI restructurer) can write, so it is never injected unsanitized.
   * @param {string} html - Stored slide HTML.
   * @returns {string}
   */
  const previewHtml = (html) => {
    if (!html) return '';
    const highlighted = String(html).replace(
      /\{\{[^}]+\}\}/g,
      '<mark class="q-slide-variable">$&</mark>'
    );
    return DOMPurify.sanitize(highlighted, { USE_PROFILES: { html: true } });
  };

  return (
    <Box
      ref={ref}
      component="section"
      aria-label={`Slide ${index + 1}`}
      sx={{
        mb: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-card)',
        opacity: isDragging ? 0.5 : 1,
        transition: 'opacity 0.2s, border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': { borderColor: 'var(--q-border-hover)', boxShadow: 'var(--q-shadow-md)' },
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: collapsed && !readOnly ? 0 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {!readOnly && (
              <Tooltip title="Drag to reorder">
                <IconButton ref={drag} sx={{ cursor: 'grab', color: 'text.secondary' }} size="small" aria-label={`Reorder slide ${index + 1}`}>
                  <DragIndicator />
                </IconButton>
              </Tooltip>
            )}
            <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 600 }}>
              Slide {index + 1}
            </Typography>
            <Chip label={SLIDE_TYPE_LABELS[slide.type] || String(slide.type || 'slide').replace(/_/g, ' ')} size="small" />
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!readOnly && (
              <>
                <Tooltip title={collapsed ? 'Expand' : 'Collapse'}>
                  <IconButton
                    onClick={() => onToggleCollapsed(slide.id)}
                    size="small"
                    aria-expanded={!collapsed}
                    aria-label={collapsed ? `Expand slide ${index + 1}` : `Collapse slide ${index + 1}`}
                  >
                    {collapsed ? <ExpandMore /> : <ExpandLess />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove slide">
                  <IconButton onClick={() => removeSlide(slide.id)} color="error" size="small" aria-label={`Remove slide ${index + 1}`}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        {readOnly ? (
          <Box
            className="ppt-slide-preview"
            sx={{
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 'var(--q-radius-lg)',
              bgcolor: 'var(--q-bg-subtle)',
              overflowWrap: 'anywhere',
              '& mark.q-slide-variable': {
                bgcolor: 'var(--q-warning-soft)',
                color: 'var(--q-warning-text)',
                borderRadius: '4px',
                px: 0.5,
              },
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml(slide.content || '') }}
          />
        ) : (
          !collapsed && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Slide Type</InputLabel>
                  <Select
                    value={slide.type}
                    label="Slide Type"
                    onChange={(e) => updateSlide(slide.id, 'type', e.target.value)}
                  >
                    <MenuItem value="title_slide">Title Slide</MenuItem>
                    <MenuItem value="content_slide">Content Slide</MenuItem>
                    <MenuItem value="image_slide">Image & Caption</MenuItem>
                    <MenuItem value="section_header">Section Header</MenuItem>
                    <MenuItem value="data_table">Data Table</MenuItem>
                    <MenuItem value="custom">Custom Layout</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 'var(--q-radius-lg)', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1,
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'var(--q-bg-subtle)',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} component="span">
                      Slide Content
                    </Typography>
                  </Box>
                  <Box sx={{ minHeight: 200 }}>
                    <EnhancedRichTextEditor
                      ref={editorRef}
                      initialContent={slide.content || '<p>Enter slide content here...</p>'}
                      onChange={handleContentChange}
                      minHeight={200}
                      placeholder="Enter slide content. Use {{variables}} for AI-generated content."
                      settings={settings}
                      showToolbar
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )
        )}
      </Box>
    </Box>
  );
}

export default DraggableSlide;
